import { keyBy } from 'lodash';
import {
    UnitGpsDto,
    MapLedgerEventView,
    MapLedgerHospitalView,
    MapLedgerUnitView,
    MapViewportEventView,
    MapViewportHospitalView,
    MapViewportUnitView,
    StationView,
    TimestampedGeoPoint,
    MapViewRequest,
    MapFullCompositeView,
} from '@mark43/cad-api';

import { action, computed, makeObservable, observable } from 'mobx';
import {
    ESRI_MAP_DEFAULT_ZOOM_LEVEL,
    ESRI_MAP_VIEW_ENTITY_ZOOM_LEVEL,
} from '../../esri/configurations';
import {
    //geoPointIsWithinBoundingBox,
    isValidLatLng,
} from '../../location/helpers';
import {
    getEventLocation,
    getMapViewportUnitLocation,
    getIsLocationPotentiallyInaccurate,
} from '../../esri/helpers/locations';
import { createPolylines, ifPolylinesContainIds } from '../../esri/helpers/polylines';
import type { GeoPoint, GeoPointAddress, MarginBounds } from '../../location/types';
import type {
    DeprecatedEsriMapFilterOptions,
    DeprecatedEsriMapFilterState,
    EsriMapFilterOptions,
    EsriMapFilterState,
    GeoPointLocation,
    MapMode,
    MapViewportDerivedPolyline,
} from '../../esri/types';
import { LayerId, EsriMapEntityTypeEnum, EsriMapContextMenuEntityTypeEnum } from '../../esri/types';
import type { AmlCallView, PhoneCallView } from '../../phone-calls/types';
import { getRelatedCall } from '../../phone-calls/utils';
import { filterViewportEntities, filterViewportEntitiesDeprecated } from '../components/helpers';
import { NearbyGeoCautionDto } from '@mark43/rms-api';
import { ViewLocationOnMap } from '../../location/hooks/types';
import { PromiseBuffer } from '../../common/promises/promise-buffer';
import { clearEntityCache } from '../../esri/helpers/entity-id-to-object-id-maps';
import { logWarn } from '../../monitoring/log';

type BootstrapState = {
    isError: boolean;
    isFetching: boolean;
    refetch: () => void;
    mapCenter?: GeoPoint;
};

interface EnabledFeatureConfig {
    showUnitLocationWhenGpsIsNotAvailable: boolean;
    showMobileDevicesOnMap: boolean;
    showMobileDevicesPolylinesOnMap: boolean;
    amlUser: boolean;
    rollOutPersistWorkspaceMapFiltersEnabled: boolean;
}

type EnabledFeaturesConfigGetter = () => EnabledFeatureConfig;

const MISSING_GPS_UNIT_FETCH_ATTEMPTS_INITIAL_VAL = 0;
const MISSING_GPS_UNIT_FETCH_ATTEMPTS_LIMIT = 5;
const DELAYED_ACTIONS_QUEUE_CAPACITY = 10;

export type EsriMapBootstrapData = {
    mapCenter: GeoPoint;
    mapViewportUnits: MapViewportUnitView[];
    mapLedgerUnits: MapLedgerUnitView[];
    mapViewportEvents: MapViewportEventView[];
    mapLedgerEvents: MapLedgerEventView[];
    stations: StationView[];
    mapViewportHospitals: MapViewportHospitalView[];
    mapLedgerHospitals: MapLedgerHospitalView[];
    cfsPriorityLabelsById: MapFullCompositeView['cfsPriorityLabelsById'];
    virtualPatrolLabel?: MapFullCompositeView['virtualPatrolLabel'];
    priorityOptions?: MapFullCompositeView['priorityOptions'];
    dispatchAreaOptions?: MapFullCompositeView['dispatchAreaOptions'];
    unitStatusOptions?: MapFullCompositeView['unitStatusOptions'];
    agencyTypeOptions?: MapFullCompositeView['agencyTypeOptions'];
};

export class EsriMapStore {
    public getEnabledFeaturesConfig: EnabledFeaturesConfigGetter;

    @observable
    public mapViewportUnitsById: Record<number, MapViewportUnitView>;
    @observable
    public mapLedgerUnitsById: Record<number, MapLedgerUnitView>;
    @observable
    public mapViewportEventsById: Record<number, MapViewportEventView>;
    @observable
    public mapLedgerEventsById: Record<number, MapLedgerEventView>;
    @observable
    public stationsById: Record<number, StationView>;
    @observable
    public mapViewportHospitalsById: Record<number, MapViewportHospitalView>;
    @observable
    public mapLedgerHospitalsById: Record<number, MapLedgerHospitalView>;
    @observable
    private userToUnitMap: Record<number, number>;

    private delayedActionsQueue: ViewLocationOnMap[];

    private missingGpsUnitFetchAttemptsById: Record<number, number>;

    @observable
    mapViewportPolylines: MapViewportDerivedPolyline[];

    @observable
    isBootstrapQuerySet: boolean;
    @observable
    mapBootstrapState?: BootstrapState;
    @observable
    deprecatedMapFilterOptions: DeprecatedEsriMapFilterOptions;
    @observable
    deprecatedMapFilterState: DeprecatedEsriMapFilterState;
    @observable
    mapFilterOptions: EsriMapFilterOptions;
    @observable
    mapFilterState: EsriMapFilterState;
    @observable
    mapCenter?: GeoPoint;
    @observable
    mapCenterForUpdate?: GeoPoint;
    @observable
    mapZoomLevel: number;
    @observable
    mapZoomForUpdate?: number;
    @observable
    marginBounds?: MarginBounds;
    @observable
    mapMode: MapMode;
    @observable
    visibleLayersIds: LayerId[];

    @observable
    isMapLedgerOpen: boolean;
    @observable
    shouldMoveMapCenterToLoggedInUnit: boolean;
    @observable
    isMapLoaded: boolean;
    @observable
    isFollowModeEnabled;
    @observable
    followingUnitId?: number;
    @observable
    locationSearchSelectedGeoPointLocation?: GeoPointLocation;
    @observable
    geoPointAddressForLocationSearch?: GeoPointAddress;
    @observable
    selectedClusterId?: number;
    @observable
    selectedUnitId?: number;
    @observable
    selectedUnitUserId?: number;
    @observable
    selectedEventId?: number;
    @observable
    selectedStationId?: number;
    @observable
    selectedHospitalId?: number;
    @observable
    selectedPhoneCallId?: number;
    @observable
    selectedAmlPhoneNumberId?: number;
    @observable
    selectedMapLedgerEntityType?: EsriMapEntityTypeEnum;
    @observable
    selectedContextMenuEntityType?: EsriMapContextMenuEntityTypeEnum;
    @observable
    contextMenuEntityId?: number;
    @observable
    amlCallEntity?: AmlCallView;
    @observable
    phoneCallEntity?: PhoneCallView;
    @observable
    nearbyGeoCautions: NearbyGeoCautionDto[];
    @observable
    selectedNearbyGeoCautionCoordinates?: GeoPoint;
    @observable
    initialGeoPoint?: GeoPoint;
    @observable
    associatedPhoneNumberId?: number;

    constructor(extras: { getEnabledFeaturesConfig: EnabledFeaturesConfigGetter, mapMode?: MapMode }) {
        this.mapViewportUnitsById = {};
        this.mapLedgerUnitsById = {};
        this.mapViewportEventsById = {};
        this.mapLedgerEventsById = {};
        this.stationsById = {};
        this.mapViewportHospitalsById = {};
        this.mapLedgerHospitalsById = {};
        this.userToUnitMap = {};
        this.mapViewportPolylines = [];
        this.delayedActionsQueue = [];
        this.missingGpsUnitFetchAttemptsById = {};

        this.mapBootstrapState = undefined;
        this.deprecatedMapFilterOptions = {};
        this.deprecatedMapFilterState = {};
        this.visibleLayersIds = [];
        this.mapFilterOptions = {};
        this.mapFilterState = {};
        this.mapCenter = undefined;
        this.mapCenterForUpdate = undefined;
        this.mapZoomLevel = ESRI_MAP_DEFAULT_ZOOM_LEVEL;
        this.mapZoomForUpdate = undefined;
        this.marginBounds = undefined;
        this.mapMode = extras.mapMode ?? 'DARK_MODE';

        this.isBootstrapQuerySet = false;
        this.isMapLedgerOpen = false;
        this.isFollowModeEnabled = false;
        this.shouldMoveMapCenterToLoggedInUnit = true;
        this.isMapLoaded = false;
        this.followingUnitId = undefined;
        this.locationSearchSelectedGeoPointLocation = undefined;
        this.geoPointAddressForLocationSearch = undefined;
        this.selectedClusterId = undefined;
        this.selectedUnitId = undefined;
        this.selectedUnitUserId = undefined;
        this.selectedEventId = undefined;
        this.selectedStationId = undefined;
        this.selectedHospitalId = undefined;
        this.selectedPhoneCallId = undefined;
        this.selectedAmlPhoneNumberId = undefined;
        this.selectedMapLedgerEntityType = undefined;
        this.selectedContextMenuEntityType = undefined;
        this.contextMenuEntityId = undefined;
        this.phoneCallEntity = undefined;
        this.amlCallEntity = undefined;
        this.nearbyGeoCautions = [];
        this.selectedNearbyGeoCautionCoordinates = undefined;
        this.initialGeoPoint = undefined;
        this.associatedPhoneNumberId = undefined;

        this.getEnabledFeaturesConfig = extras.getEnabledFeaturesConfig;
        makeObservable(this);
    }

    // --- get mapViewport and mapLedger Unit/Event

    @computed
    get mapViewportUnits(): MapViewportUnitView[] {
        return Object.values(this.mapViewportUnitsById);
    }
    @computed
    get mapLedgerUnits(): MapLedgerUnitView[] {
        return Object.values(this.mapLedgerUnitsById);
    }

    @computed
    get mapViewportEvents(): MapViewportEventView[] {
        return Object.values(this.mapViewportEventsById);
    }
    @computed
    get mapLedgerEvents(): MapLedgerEventView[] {
        return Object.values(this.mapLedgerEventsById);
    }

    get isDelayedActionsQueueEmpty(): boolean {
        return this.delayedActionsQueue.length === 0;
    }

    public getMapViewportUnit = (id: number): MapViewportUnitView | undefined => {
        return this.mapViewportUnitsById[id];
    };

    public getUnitIdByUserId = (userId: number): number | undefined => {
        return this.userToUnitMap[userId];
    };

    public getMapLedgerUnit = (id: number): MapLedgerUnitView | undefined => {
        return this.mapLedgerUnitsById[id];
    };

    public getMapViewportEvent = (id: number): MapViewportEventView | undefined => {
        return this.mapViewportEventsById[id];
    };

    public getMapLedgerEvent = (id: number): MapLedgerEventView | undefined => {
        return this.mapLedgerEventsById[id];
    };

    public eventHasAssociatedPhoneCall = (eventId: number): boolean => {
        return (
            this.getMapLedgerEvent(eventId)?.phoneCalls.some(
                (i) => i.phoneNumberId === this.associatedPhoneNumberId
            ) ?? false
        );
    };

    // --- set mapViewport and mapLedger Unit/Event. when set entity by setMap*Unit/Event, some fields will be automatically updated.
    // by default set unit/event will replace entire *ById object. (only setMapViewportUnits may be used for upsert gps location, which will only update given 'mapViewportUnits')

    @action
    setMapViewportUnits = (mapViewportUnits: MapViewportUnitView[], upsert = false) => {
        const currentTimestamp = this.getCurrentTimestamp();
        const updatedUserToUnitMap: Record<number, number> = {};
        const updatedMapViewportUnitsById = mapViewportUnits.reduce<
            Record<number, MapViewportUnitView>
        >((acc, cur) => {
            const { unitUserLocations, entityId } = cur;
            for (const unitUserLocation of unitUserLocations) {
                updatedUserToUnitMap[unitUserLocation.entityId] = entityId;
                if (upsert) {
                    this.userToUnitMap[unitUserLocation.entityId] = entityId;
                }
            }
            const updatedMapViewPortUnit = this.updateMapViewportUnitFields(currentTimestamp, cur);
            if (upsert) {
                this.mapViewportUnitsById[cur.entityId] = updatedMapViewPortUnit;
            }
            acc[cur.entityId] = updatedMapViewPortUnit;
            return acc;
        }, {});

        // when upsert = true, this.mapViewportUnitsById is already updated in the mapViewportUnits.reduce loop.
        if (!upsert) {
            this.userToUnitMap = updatedUserToUnitMap;
            this.mapViewportUnitsById = updatedMapViewportUnitsById;
        }
    };

    @action
    setMapLedgerUnits = (mapLedgerUnits: MapLedgerUnitView[]) => {
        this.mapLedgerUnitsById = keyBy(mapLedgerUnits, 'entityId');
    };

    @action
    setMapViewportEvents = (mapViewportEvents: MapViewportEventView[]) => {
        const mapViewportEventsById = mapViewportEvents.reduce<
            Record<number, MapViewportEventView>
        >((acc, cur) => {
            acc[cur.entityId] = this.updateMapViewportEventFields(cur);
            return acc;
        }, {});
        this.mapViewportEventsById = mapViewportEventsById;
    };

    @action
    setMapLedgerEvents = (mapLedgerEvents: MapLedgerEventView[]) => {
        this.mapLedgerEventsById = keyBy(mapLedgerEvents, 'entityId');
    };

    // --- update event/unit entity

    private getCurrentTimestamp = () => new Date().getTime();

    private updateMapViewportUnitFields = (
        currentTimestamp: number,
        mapViewportUnit: MapViewportUnitView
    ): MapViewportUnitView => {
        const unitLocation = getMapViewportUnitLocation(
            mapViewportUnit,
            this.getEnabledFeaturesConfig().showUnitLocationWhenGpsIsNotAvailable
        );
        const unitLocationTimestamp = unitLocation?.timestamp;
        const isUnitLocationPotentiallyInaccurate = getIsLocationPotentiallyInaccurate(
            currentTimestamp,
            unitLocationTimestamp
        );

        const unitUserLocations = mapViewportUnit.unitUserLocations?.map((unitUserLocation) => {
            const isUserLocationPotentiallyInaccurate = getIsLocationPotentiallyInaccurate(
                currentTimestamp,
                // users only have gpsLocation
                unitUserLocation.gpsLocation?.timestamp
            );
            return {
                ...unitUserLocation,
                isLocationPotentiallyInaccurate: isUserLocationPotentiallyInaccurate,
            };
        });

        const visible = this.getEnabledFeaturesConfig().rollOutPersistWorkspaceMapFiltersEnabled
            ? filterViewportEntities({
                  mapFilterState: this.mapFilterState,
                  viewportEntity: mapViewportUnit,
                  entityType: EsriMapEntityTypeEnum.UNIT,
                  mapFilterOptions: this.mapFilterOptions,
              })
            : filterViewportEntitiesDeprecated({
                  mapFilterState: this.deprecatedMapFilterState,
                  viewportEntity: mapViewportUnit,
                  entityType: EsriMapEntityTypeEnum.UNIT,
                  mapFilterOptions: this.deprecatedMapFilterOptions,
              });

        // TODO: MAP RND-11325 validate if bounding box check is required
        // if (visible && this.marginBounds && unitLocation) {
        //     visible = geoPointIsWithinBoundingBox(
        //         unitLocation,
        //         this.marginBounds.nw,
        //         this.marginBounds.se
        //     );
        // }

        return {
            ...mapViewportUnit,
            unitUserLocations,
            isLocationPotentiallyInaccurate: isUnitLocationPotentiallyInaccurate,
            visible,
        };
    };

    private updateMapViewportEventFields = (
        mapViewportEvent: MapViewportEventView
    ): MapViewportEventView => {
        const visible = this.getEnabledFeaturesConfig().rollOutPersistWorkspaceMapFiltersEnabled
            ? filterViewportEntities({
                  mapFilterState: this.mapFilterState,
                  viewportEntity: mapViewportEvent,
                  entityType: EsriMapEntityTypeEnum.EVENT,
                  mapFilterOptions: this.mapFilterOptions,
              })
            : filterViewportEntitiesDeprecated({
                  mapFilterState: this.deprecatedMapFilterState,
                  viewportEntity: mapViewportEvent,
                  entityType: EsriMapEntityTypeEnum.EVENT,
                  mapFilterOptions: this.deprecatedMapFilterOptions,
              });

        // TODO: MAP RND-11325 validate if bounding box check is required
        // if (visible && this.marginBounds && mapViewportEvent.geoLocation) {
        //     visible = geoPointIsWithinBoundingBox(
        //         mapViewportEvent.geoLocation,
        //         this.marginBounds.nw,
        //         this.marginBounds.se
        //     );
        // }

        return {
            ...mapViewportEvent,
            visible,
        };
    };

    @action
    refreshMapViewportUnitsPotentialInaccuracy = () => {
        if (this.mapViewportUnits?.length) {
            // setMapViewportUnits will automatically update isLocationPotentiallyInaccurate field
            this.setMapViewportUnits(this.mapViewportUnits);
        }
    };

    @action
    evictUnitEntities = (unitIds: number[]) => {
        const idsToRemove = new Set(unitIds);

        const isSelectedUnitBeingRemoved =
            this.selectedUnitId && idsToRemove.has(this.selectedUnitId);
        if (isSelectedUnitBeingRemoved) {
            this.clearSelectedMapLedgerEntity();
        }

        this.setMapViewportUnits(
            this.mapViewportUnits.filter((unit) => !idsToRemove.has(unit.entityId))
        );
        this.setMapLedgerUnits(
            Object.values(this.mapLedgerUnits).filter((unit) => !idsToRemove.has(unit.entityId))
        );

        if (
            !isSelectedUnitBeingRemoved &&
            ifPolylinesContainIds(unitIds, this.mapViewportPolylines)
        ) {
            this.refreshPolylines(this.selectedMapLedgerEntityType);
        }
    };

    @action
    upsertMapViewportUnits = (mapViewportUnitViews: MapViewportUnitView[]) => {
        this.setMapViewportUnits(
            this.getUpsertedEntities(
                mapViewportUnitViews,
                this.mapViewportUnitsById,
                this.upsertMapViewportUnitCallback
            )
        );

        if (
            ifPolylinesContainIds(
                mapViewportUnitViews.map((entity) => entity.entityId),
                this.mapViewportPolylines
            )
        ) {
            this.refreshPolylines(this.selectedMapLedgerEntityType);
        }
    };

    private upsertMapViewportUnitCallback = (entity: MapViewportUnitView, isNewEntity: boolean) => {
        // unit/user gpsLocation will not be returned when fetch the unit/user data from nudge endpoint.
        // all gpsLocations are set by gps websocket. To preserve gps data, copy existing unit's mapViewportUnit.gpsLocation and users' mapViewportUnit.unitUserLocations
        if (isNewEntity) {
            return entity;
        }
        const newUnitUserLocations = [];

        const existingMapViewportUnit = this.mapViewportUnitsById[entity.entityId];
        if (existingMapViewportUnit?.unitUserLocations.length) {
            const existingMapViewportUnitByUserId = keyBy(
                existingMapViewportUnit.unitUserLocations,
                'entityId'
            );
            for (const newUnitUserLocation of entity.unitUserLocations) {
                if (existingMapViewportUnitByUserId[newUnitUserLocation.entityId]) {
                    newUnitUserLocations.push({
                        ...newUnitUserLocation,
                        ...existingMapViewportUnitByUserId[newUnitUserLocation.entityId],
                    });
                }
            }
        }

        return {
            ...entity,
            gpsLocation: existingMapViewportUnit.gpsLocation,
            unitUserLocations: newUnitUserLocations,
        };
    };

    private prepareMapviewportUnitForGpsLocationUpdate = (
        gps: UnitGpsDto,
        promiseBuffer: PromiseBuffer<MapViewRequest>
    ): MapViewportUnitView | undefined => {
        const { unitId, userId } = gps;
        const viewportUnit = this.mapViewportUnitsById[unitId];

        const getUpdatedGpsPosition = (u?: MapViewportUnitView): TimestampedGeoPoint => {
            return {
                ...u?.gpsLocation,
                latitude: gps.lat,
                longitude: gps.lng,
                timestamp: gps.dateTime
                    ? new Date(gps.dateTime).getTime()
                    : this.getCurrentTimestamp(),
            };
        };

        if (viewportUnit) {
            const { entityId, unitUserLocations, gpsLocation } = viewportUnit;
            const newUnitUserLocations = userId
                ? unitUserLocations.map((unitUserLocation) => {
                      if (unitUserLocation.entityId === userId) {
                          this.userToUnitMap[userId] = entityId;
                          return { ...unitUserLocation, gpsLocation: getUpdatedGpsPosition() };
                      }
                      return unitUserLocation;
                  })
                : unitUserLocations;
            const newGpsLocation = userId ? gpsLocation : getUpdatedGpsPosition(viewportUnit);
            return {
                ...viewportUnit,
                gpsLocation: newGpsLocation,
                unitUserLocations: newUnitUserLocations,
            };
        } else {
            // if the viewportUnit is not found but unitId is received again from a gps
            // the missing unitId will be added to the promiseBuffer -> it will try to fetch the info for the missing unit again
            // a limit for the refetching attempts added in order not to overload the BE

            if (!this.missingGpsUnitFetchAttemptsById[unitId]) {
                this.missingGpsUnitFetchAttemptsById[unitId] =
                    MISSING_GPS_UNIT_FETCH_ATTEMPTS_INITIAL_VAL;
            }

            if (
                this.missingGpsUnitFetchAttemptsById[unitId] < MISSING_GPS_UNIT_FETCH_ATTEMPTS_LIMIT
            ) {
                promiseBuffer.enqueue({
                    eventIds: [],
                    unitIds: [unitId],
                    hospitalIds: [],
                    dispatchAreaIds: [],
                });
                this.missingGpsUnitFetchAttemptsById[unitId]++;
            } else {
                logWarn('Missing gps unit fetch attempts reached the permissible limit', {
                    missingGpsUnitId: unitId,
                });
            }

            return undefined;
        }
    };

    @action
    updateMapViewportUnitGpsLocations = (
        gpsUpdates: UnitGpsDto[],
        promiseBuffer: PromiseBuffer<MapViewRequest>
    ) => {
        const pendingMapViewportUnits = [];
        let shouldUpdatePolylines = false;
        let selectedUnitGpsUpdate;
        for (const gpsUpdate of gpsUpdates) {
            const pendingMapViewportUnit = this.prepareMapviewportUnitForGpsLocationUpdate(
                gpsUpdate,
                promiseBuffer
            );
            if (pendingMapViewportUnit) {
                pendingMapViewportUnits.push(pendingMapViewportUnit);
                if (
                    !selectedUnitGpsUpdate &&
                    pendingMapViewportUnit.entityId === this.selectedUnitId
                ) {
                    selectedUnitGpsUpdate = gpsUpdate;
                }
            }
        }
        if (pendingMapViewportUnits.length) {
            this.setMapViewportUnits(
                pendingMapViewportUnits,
                // upsert only for given mapViewportUnits
                true
            );
            shouldUpdatePolylines = ifPolylinesContainIds(
                pendingMapViewportUnits.map(
                    (pendingMapViewportUnit) => pendingMapViewportUnit.entityId
                ),
                this.mapViewportPolylines
            );
            if (shouldUpdatePolylines) {
                this.refreshPolylines(this.selectedMapLedgerEntityType);
            }
        }
        if (gpsUpdates?.length) {
            logWarn('gps update processed', {
                numOfGpsUpdates: gpsUpdates.length,
                numOfPendingMapViewportUnits: pendingMapViewportUnits.length,
                selectedUnitId: this.selectedUnitId,
                selectedUnitGpsUpdate,
                ploylineUpdated: shouldUpdatePolylines,
            });
        }
    };

    @action
    upsertMapLedgerUnits = (mapLedgerUnits: MapLedgerUnitView[]) => {
        this.setMapLedgerUnits(this.getUpsertedEntities(mapLedgerUnits, this.mapLedgerUnitsById));
    };

    private getUpsertedEntities = <T extends { entityId: number }>(
        entities: T[],
        entityIdMap: Record<number, T>,
        updateEntityCallback?: (entity: T, isNewEntity: boolean) => T
    ): T[] => {
        const existingEntities = { ...entityIdMap };
        const entitiesToAdd: T[] = [];

        for (const entity of entities) {
            const existingEntity = existingEntities[entity.entityId];
            if (existingEntity) {
                existingEntities[entity.entityId] = updateEntityCallback
                    ? updateEntityCallback(entity, false)
                    : entity;
            } else {
                entitiesToAdd.push(entity);
            }
        }

        return [...Object.values(existingEntities), ...entitiesToAdd];
    };

    @action
    evictEventEntities = (eventIds: number[]) => {
        const idsToRemove = new Set(eventIds);

        const isSelectedEventBeingRemoved =
            this.selectedEventId && idsToRemove.has(this.selectedEventId);

        if (isSelectedEventBeingRemoved) {
            // get eventLocation before it gets removed from mapLedgerEventsById
            const eventLocation = this.selectedEventId
                ? getEventLocation(this.mapLedgerEventsById[this.selectedEventId])
                : undefined;
            if (eventLocation && isValidLatLng(eventLocation.latitude, eventLocation.longitude)) {
                this.setSelectedMapLedgerEntity(EsriMapEntityTypeEnum.GEO_POINT_LOCATION, {
                    latitude: eventLocation.latitude!,
                    longitude: eventLocation.longitude!,
                });
                this.clearPolylines();
            } else {
                this.clearSelectedMapLedgerEntity();
            }
        }

        this.setMapViewportEvents(
            this.mapViewportEvents.filter((event) => !idsToRemove.has(event.entityId))
        );
        this.setMapLedgerEvents(
            Object.values(this.mapLedgerEvents).filter((event) => !idsToRemove.has(event.entityId))
        );

        if (
            !isSelectedEventBeingRemoved &&
            ifPolylinesContainIds(eventIds, this.mapViewportPolylines)
        ) {
            this.refreshPolylines(this.selectedMapLedgerEntityType);
        }
    };

    @action
    upsertMapViewportEvents = (mapViewportEvents: MapViewportEventView[]) => {
        this.setMapViewportEvents(
            this.getUpsertedEntities(mapViewportEvents, this.mapViewportEventsById)
        );

        if (
            ifPolylinesContainIds(
                mapViewportEvents.map((entity) => entity.entityId),
                this.mapViewportPolylines
            )
        ) {
            this.refreshPolylines(this.selectedMapLedgerEntityType);
        }
    };

    @action
    upsertMapLedgerEvents = (mapLedgerEvents: MapLedgerEventView[]) => {
        this.setMapLedgerEvents(
            this.getUpsertedEntities(mapLedgerEvents, this.mapLedgerEventsById)
        );
    };

    // --- polylines

    @action
    setMapViewportPolylines = (mapViewportPolylines: MapViewportDerivedPolyline[]) => {
        this.mapViewportPolylines = mapViewportPolylines;
    };

    @action
    refreshPolylines = (entityType?: EsriMapEntityTypeEnum) => {
        const selectedEventId = this.selectedEventId;
        const selectedUnit = this.selectedUnitId
            ? this.mapViewportUnitsById[this.selectedUnitId]
            : undefined;
        const assignedEvent = selectedUnit?.assignedEventId
            ? this.mapViewportEventsById[selectedUnit.assignedEventId]
            : undefined;

        const {
            amlUser,
            showMobileDevicesOnMap,
            showMobileDevicesPolylinesOnMap,
            showUnitLocationWhenGpsIsNotAvailable,
        } = this.getEnabledFeaturesConfig();

        const centralEvent = selectedEventId
            ? this.mapViewportEventsById[selectedEventId]
            : undefined;

        this.mapViewportPolylines = createPolylines({
            showMobileDevicesPolylinesOnMap:
                showMobileDevicesOnMap && showMobileDevicesPolylinesOnMap,
            showUnitLocationWhenGpsIsNotAvailable,
            activeGeoPoint: this.locationSearchSelectedGeoPointLocation,
            mapViewportUnitsById: this.mapViewportUnitsById,
            phoneCall: this.phoneCallEntity,
            amlCall: this.amlCallEntity,
            assignedEvent,
            selectedUnit,
            centralEvent,
            entityType,
            amlUser,
        });
    };

    @action
    clearPolylines = () => {
        this.setMapViewportPolylines([]);
        clearEntityCache('POLYLINES');
    };

    // --- set other state and select entity

    @action
    setIsBootstrapQuerySet = (bootstrapQuerySet: boolean) => {
        this.isBootstrapQuerySet = bootstrapQuerySet;
    };

    @action
    setMapBootstrapState = (bootstrapState: BootstrapState) => {
        this.mapBootstrapState = bootstrapState;
    };

    @action
    setDeprecatedMapFilterOptions = (filterOptions: DeprecatedEsriMapFilterOptions) => {
        this.deprecatedMapFilterOptions = filterOptions;
    };

    @action
    setDeprecatedMapFilterState = (filterState: DeprecatedEsriMapFilterState) => {
        this.deprecatedMapFilterState = filterState;
    };

    @action
    setMapFilterOptions = (filterOptions: EsriMapFilterOptions) => {
        this.mapFilterOptions = filterOptions;
    };

    @action
    setMapFilterState = (filterState: EsriMapFilterState) => {
        this.mapFilterState = filterState;
    };

    @action
    setMapCenter = (geoPoint?: GeoPoint) => {
        this.mapCenter = geoPoint;
    };

    @action
    setMapCenterForUpdate = (geoPoint?: GeoPoint, resetZoomLevel?: boolean) => {
        this.mapCenterForUpdate = geoPoint;
        if (resetZoomLevel) {
            this.mapZoomForUpdate = ESRI_MAP_VIEW_ENTITY_ZOOM_LEVEL;
        }
    };

    @action
    setMapZoomLevel = (zoom: number) => {
        this.mapZoomLevel = zoom;
    };

    @action
    setMapZoomForUpdate = (zoomLevel?: number) => {
        this.mapZoomForUpdate = zoomLevel;
    };

    @action
    setMarginBounds = (marginBounds?: MarginBounds) => {
        this.marginBounds = marginBounds;
    };

    @action
    setLocationSearchSelectedGeoPointLocation = (geoLocation?: GeoPointLocation) => {
        this.locationSearchSelectedGeoPointLocation = geoLocation;
    };

    @action
    setGeoPointAddressForLocationSearch = (geoPointAddress?: GeoPointAddress) => {
        this.geoPointAddressForLocationSearch = geoPointAddress;
    };

    @action
    clearGeoPointLocationAndAddress = () => {
        this.locationSearchSelectedGeoPointLocation = undefined;
        this.geoPointAddressForLocationSearch = undefined;
    };

    @action
    setNearbyGeoCautions = (nearbyGeoCautions: NearbyGeoCautionDto[]) => {
        this.nearbyGeoCautions = nearbyGeoCautions;
    };

    @action
    clearNearbyGeoCautions = () => {
        this.nearbyGeoCautions = [];
    };

    @action
    setSelectedNearbyGeoCautionCoordinates = (selectedNearbyGeoCautionCoordinates: GeoPoint) => {
        this.selectedNearbyGeoCautionCoordinates = selectedNearbyGeoCautionCoordinates;
    };

    @action
    clearSelectedNearbyGeoCautionCoordinates = () => {
        this.selectedNearbyGeoCautionCoordinates = undefined;
    };

    @action
    setIsMapLedgerOpen = (isOpen: boolean) => {
        this.isMapLedgerOpen = isOpen;
    };

    @action
    openEntityContextMenu = (entityType: EsriMapContextMenuEntityTypeEnum, entityId: number) => {
        switch (entityType) {
            case EsriMapContextMenuEntityTypeEnum.EVENT: {
                this.selectedContextMenuEntityType = EsriMapContextMenuEntityTypeEnum.EVENT;
                this.contextMenuEntityId = entityId;
                break;
            }
            case EsriMapContextMenuEntityTypeEnum.UNIT: {
                this.selectedContextMenuEntityType = EsriMapContextMenuEntityTypeEnum.UNIT;
                this.contextMenuEntityId = entityId;
                break;
            }
        }
    };

    @action
    closeEntityContextMenu = () => {
        this.selectedContextMenuEntityType = undefined;
        this.contextMenuEntityId = undefined;
    };

    @action
    setIsFollowModeEnabled = (isEnabled: boolean) => {
        this.isFollowModeEnabled = isEnabled;
    };

    @action
    setFollowingUnitId = (unitId?: number) => {
        this.followingUnitId = unitId;
    };

    @action
    openEntityDetails = () => {
        if (
            !this.isMapLedgerOpen &&
            this.selectedMapLedgerEntityType &&
            this.selectedMapLedgerEntityType !== EsriMapEntityTypeEnum.CLUSTER
        ) {
            this.setIsMapLedgerOpen(true);
        }
    };

    @action
    setAmlCallEntity = (amlCallEntity?: AmlCallView) => {
        this.amlCallEntity = amlCallEntity;
    };

    @action
    setPhoneCallEntity = (phoneCall?: PhoneCallView) => {
        this.phoneCallEntity = phoneCall;
    };

    @computed
    get stations(): StationView[] {
        return Object.values(this.stationsById);
    }

    @action
    setStations = (stations: StationView[]) => {
        this.stationsById = keyBy(stations, 'id');
    };

    public getStation = (id?: number): StationView | undefined => {
        return id ? this.stationsById[id] : undefined;
    };

    @computed
    get mapViewportHospitals(): MapViewportHospitalView[] {
        return Object.values(this.mapViewportHospitalsById);
    }
    @computed
    get mapLedgerHospitals(): MapLedgerHospitalView[] {
        return Object.values(this.mapLedgerHospitalsById);
    }
    @computed
    get selectedMapViewportHospital(): MapViewportHospitalView | undefined {
        if (this.selectedHospitalId) {
            return this.mapViewportHospitalsById[this.selectedHospitalId];
        }
        return undefined;
    }
    @computed
    get selectedMapLedgerHospital(): MapLedgerHospitalView | undefined {
        if (this.selectedHospitalId) {
            return this.mapLedgerHospitalsById[this.selectedHospitalId];
        }
        return undefined;
    }

    public getMapViewportHospital = (id: number): MapViewportHospitalView | undefined => {
        return this.mapViewportHospitalsById[id];
    };

    public getMapLedgerHospital = (id: number): MapLedgerHospitalView | undefined => {
        return this.mapLedgerHospitalsById[id];
    };

    @action
    setMapViewportHospitals = (mapViewportHospitals: MapViewportHospitalView[]) => {
        this.mapViewportHospitalsById = keyBy(mapViewportHospitals, 'entityId');
    };

    @action
    setMapLedgerHospitals = (mapLedgerHospitals: MapLedgerHospitalView[]) => {
        this.mapLedgerHospitalsById = keyBy(mapLedgerHospitals, 'entityId');
    };

    @action
    upsertMapViewportHospitals = (mapViewportHospitals: MapViewportHospitalView[]) => {
        for (const mapViewportHospital of mapViewportHospitals) {
            this.mapViewportHospitalsById[mapViewportHospital.entityId] = mapViewportHospital;
        }
    };

    @action
    upsertMapLedgerHospitals = (mapLedgerHospitals: MapLedgerHospitalView[]) => {
        for (const mapLedgerHospital of mapLedgerHospitals) {
            this.mapLedgerHospitalsById[mapLedgerHospital.entityId] = mapLedgerHospital;
        }
    };

    @action
    setMapMode = (mapMode: MapMode) => {
        this.mapMode = mapMode;
    };

    @action
    setVisibleLayersIds = (layersIds: LayerId[]) => {
        this.visibleLayersIds = layersIds;
    };

    @action
    setSelectedUnitId = (unitId?: number) => {
        this.openEntityDetails();
        this.selectedUnitId = unitId;
    };

    @action
    setSelectedClusterId = (clusterId?: number) => {
        this.selectedClusterId = clusterId;
    };

    @computed
    get selectedMapLedgerUnit(): MapLedgerUnitView | undefined {
        if (
            this.selectedMapLedgerEntityType === EsriMapEntityTypeEnum.UNIT &&
            this.selectedUnitId
        ) {
            return this.mapLedgerUnitsById[this.selectedUnitId];
        } else if (
            this.selectedMapLedgerEntityType === EsriMapEntityTypeEnum.USER &&
            this.selectedUnitUserId
        ) {
            return this.mapLedgerUnitsById[this.userToUnitMap[this.selectedUnitUserId]];
        }
        return undefined;
    }

    @action
    setSelectedEventId = (eventId?: number) => {
        this.openEntityDetails();
        this.selectedEventId = eventId;
    };

    @computed
    get selectedMapLedgerEvent(): MapLedgerEventView | undefined {
        if (this.selectedEventId) {
            return this.mapLedgerEventsById[this.selectedEventId];
        }

        return undefined;
    }

    @computed
    get selectedStation(): StationView | undefined {
        if (this.selectedStationId) {
            return this.stationsById[this.selectedStationId];
        }

        return undefined;
    }

    @action
    setSelectedMapLedgerEntity = (
        entityType: EsriMapEntityTypeEnum,
        entityReference: number | GeoPointLocation | GeoPointAddress
    ) => {
        switch (entityType) {
            case EsriMapEntityTypeEnum.EVENT: {
                if (typeof entityReference !== 'number') {
                    break;
                }
                this.selectedMapLedgerEntityType = EsriMapEntityTypeEnum.EVENT;
                this.selectedEventId = entityReference;

                const hasAssociatedPhoneCall = this.eventHasAssociatedPhoneCall(entityReference);

                if (!hasAssociatedPhoneCall) {
                    this.selectedPhoneCallId = undefined;
                    this.amlCallEntity = undefined;
                    this.phoneCallEntity = undefined;
                }

                this.selectedClusterId = undefined;
                this.selectedUnitId = undefined;
                this.selectedUnitUserId = undefined;
                this.selectedStationId = undefined;
                this.selectedHospitalId = undefined;
                this.selectedAmlPhoneNumberId = undefined;
                this.selectedPhoneCallId = undefined;
                this.initialGeoPoint = undefined;
                this.clearGeoPointLocationAndAddress();
                this.clearSelectedNearbyGeoCautionCoordinates();

                this.openEntityDetails();
                break;
            }
            case EsriMapEntityTypeEnum.UNIT: {
                this.selectedMapLedgerEntityType = EsriMapEntityTypeEnum.UNIT;
                this.selectedUnitId = entityReference as number;
                this.selectedEventId =
                    this.mapViewportUnitsById[this.selectedUnitId]?.assignedEventId;

                this.selectedUnitUserId = undefined;
                this.selectedStationId = undefined;
                this.selectedHospitalId = undefined;
                this.selectedAmlPhoneNumberId = undefined;
                this.selectedPhoneCallId = undefined;
                this.initialGeoPoint = undefined;
                this.associatedPhoneNumberId = undefined;
                this.clearGeoPointLocationAndAddress();
                this.clearNearbyGeoCautions();
                this.clearSelectedNearbyGeoCautionCoordinates();

                this.openEntityDetails();
                break;
            }
            case EsriMapEntityTypeEnum.USER: {
                this.selectedMapLedgerEntityType = EsriMapEntityTypeEnum.USER;
                this.selectedUnitUserId = entityReference as number;
                this.selectedUnitId = this.userToUnitMap[this.selectedUnitUserId];
                this.selectedEventId =
                    this.mapViewportUnitsById[this.selectedUnitId]?.assignedEventId;

                this.selectedStationId = undefined;
                this.selectedHospitalId = undefined;
                this.selectedAmlPhoneNumberId = undefined;
                this.selectedPhoneCallId = undefined;
                this.initialGeoPoint = undefined;
                this.associatedPhoneNumberId = undefined;
                this.clearGeoPointLocationAndAddress();
                this.clearNearbyGeoCautions();
                this.clearSelectedNearbyGeoCautionCoordinates();

                this.openEntityDetails();
                break;
            }
            case EsriMapEntityTypeEnum.CLUSTER: {
                // only set selectedClusterId. no other actions

                this.selectedClusterId = entityReference as number;

                break;
            }
            case EsriMapEntityTypeEnum.STATION: {
                this.selectedMapLedgerEntityType = EsriMapEntityTypeEnum.STATION;
                this.selectedStationId = entityReference as number;

                this.selectedClusterId = undefined;
                this.selectedUnitId = undefined;
                this.selectedUnitUserId = undefined;
                this.selectedEventId = undefined;
                this.selectedHospitalId = undefined;
                this.selectedAmlPhoneNumberId = undefined;
                this.selectedPhoneCallId = undefined;
                this.phoneCallEntity = undefined;
                this.amlCallEntity = undefined;
                this.initialGeoPoint = undefined;
                this.associatedPhoneNumberId = undefined;
                this.clearGeoPointLocationAndAddress();
                this.clearNearbyGeoCautions();
                this.clearSelectedNearbyGeoCautionCoordinates();

                this.openEntityDetails();
                break;
            }
            case EsriMapEntityTypeEnum.HOSPITAL: {
                this.selectedMapLedgerEntityType = EsriMapEntityTypeEnum.HOSPITAL;
                this.selectedHospitalId = entityReference as number;

                this.selectedClusterId = undefined;
                this.selectedUnitId = undefined;
                this.selectedUnitUserId = undefined;
                this.selectedEventId = undefined;
                this.selectedStationId = undefined;
                this.selectedAmlPhoneNumberId = undefined;
                this.selectedPhoneCallId = undefined;
                this.phoneCallEntity = undefined;
                this.amlCallEntity = undefined;
                this.initialGeoPoint = undefined;
                this.associatedPhoneNumberId = undefined;
                this.clearGeoPointLocationAndAddress();
                this.clearNearbyGeoCautions();
                this.clearSelectedNearbyGeoCautionCoordinates();

                this.openEntityDetails();
                break;
            }
            case EsriMapEntityTypeEnum.GEO_POINT_LOCATION: {
                this.selectedMapLedgerEntityType = EsriMapEntityTypeEnum.GEO_POINT_LOCATION;
                if (typeof entityReference !== 'number' && 'entityId' in entityReference) {
                    this.locationSearchSelectedGeoPointLocation =
                        entityReference as GeoPointLocation;
                    this.geoPointAddressForLocationSearch = undefined;
                } else if (
                    typeof entityReference !== 'number' &&
                    isValidLatLng(entityReference.latitude, entityReference.longitude)
                ) {
                    this.setGeoPointAddressForLocationSearch({
                        latitude: entityReference.latitude,
                        longitude: entityReference.longitude,
                        fullAddress: entityReference.fullAddress,
                        preferLatLngSourceLocation: entityReference.preferLatLngSourceLocation,
                    });
                    this.initialGeoPoint = {
                        latitude: entityReference.latitude,
                        longitude: entityReference.longitude,
                    };
                    this.locationSearchSelectedGeoPointLocation = undefined;
                    this.associatedPhoneNumberId = undefined;
                }

                this.selectedClusterId = undefined;
                this.selectedUnitId = undefined;
                this.selectedUnitUserId = undefined;
                this.selectedEventId = undefined;
                this.selectedStationId = undefined;
                this.selectedHospitalId = undefined;
                this.selectedAmlPhoneNumberId = undefined;
                this.selectedPhoneCallId = undefined;
                this.clearSelectedNearbyGeoCautionCoordinates();

                this.openEntityDetails();
                break;
            }
            case EsriMapEntityTypeEnum.AML_CALL: {
                this.selectedMapLedgerEntityType = EsriMapEntityTypeEnum.AML_CALL;
                this.selectedAmlPhoneNumberId = entityReference as number;

                this.selectedClusterId = undefined;
                this.selectedStationId = undefined;
                this.selectedHospitalId = undefined;
                this.selectedPhoneCallId = undefined;
                this.initialGeoPoint = undefined;
                this.clearNearbyGeoCautions();
                this.clearSelectedNearbyGeoCautionCoordinates();

                this.openEntityDetails();
                break;
            }
            case EsriMapEntityTypeEnum.PHONE_CALL: {
                this.selectedMapLedgerEntityType = EsriMapEntityTypeEnum.PHONE_CALL;
                this.selectedPhoneCallId = entityReference as number;

                this.selectedClusterId = undefined;
                this.selectedStationId = undefined;
                this.selectedHospitalId = undefined;
                this.selectedAmlPhoneNumberId = undefined;
                this.initialGeoPoint = undefined;
                this.clearNearbyGeoCautions();
                this.clearSelectedNearbyGeoCautionCoordinates();
                this.clearGeoPointLocationAndAddress();

                this.openEntityDetails();
                break;
            }
        }
    };

    @action
    setRelatedAmlRapidSOSCallsEntities = ({
        phoneNumberIds,
        activePhoneCallsList,
        amlCallsList,
    }: {
        phoneNumberIds: (number | undefined)[];
        activePhoneCallsList: PhoneCallView[];
        amlCallsList: AmlCallView[];
    }) => {
        const relatedE911Call = getRelatedCall(
            phoneNumberIds,
            activePhoneCallsList
        ) as PhoneCallView;
        const relatedRapidSOSCall = getRelatedCall(phoneNumberIds, amlCallsList) as AmlCallView;
        this.setPhoneCallEntity(relatedE911Call);
        this.setAmlCallEntity(relatedRapidSOSCall);
    };

    @action
    refreshEntitiesAndPolylines = ({
        shouldMoveCenter,
        shouldResetZoomLevel,
        shouldRefreshLedgerEntity,
        entityType,
        data,
    }: {
        shouldMoveCenter?: boolean;
        shouldResetZoomLevel?: boolean;
        shouldRefreshLedgerEntity?: boolean;
        entityType?: EsriMapEntityTypeEnum;
        data?: {
            entityData?: number | GeoPointLocation | GeoPointAddress;
            mapCenterLatitude?: number;
            mapCenterLongitude?: number;
        };
    }) => {
        if (shouldRefreshLedgerEntity && entityType && data?.entityData) {
            this.setSelectedMapLedgerEntity(entityType, data.entityData);
        }

        if (shouldMoveCenter && isValidLatLng(data?.mapCenterLatitude, data?.mapCenterLongitude)) {
            this.setMapCenterForUpdate(
                {
                    latitude: data?.mapCenterLatitude,
                    longitude: data?.mapCenterLongitude,
                } as GeoPoint,
                shouldResetZoomLevel
            );
        }
        this.refreshPolylines(entityType);
    };

    @action
    clearSelectedMapLedgerEntity = (isDraft?: boolean) => {
        this.selectedMapLedgerEntityType = undefined;

        if (this.selectedEventId || isDraft) {
            this.amlCallEntity = undefined;
            this.phoneCallEntity = undefined;
            this.associatedPhoneNumberId = undefined;
            clearEntityCache('AML');
        }
        this.selectedEventId = undefined;
        this.selectedClusterId = undefined;
        this.selectedUnitId = undefined;
        this.selectedUnitUserId = undefined;
        this.selectedStationId = undefined;
        this.selectedAmlPhoneNumberId = undefined;
        this.selectedPhoneCallId = undefined;
        this.initialGeoPoint = undefined;
        this.clearGeoPointLocationAndAddress();
        this.clearPolylines();
        this.clearNearbyGeoCautions();
        this.clearSelectedNearbyGeoCautionCoordinates();

        this.setIsMapLedgerOpen(false);
    };

    @action
    updateMapView = () => {
        this.setMapViewportUnits(this.mapViewportUnits);
        this.setMapViewportEvents(this.mapViewportEvents);

        this.refreshPolylines(this.selectedMapLedgerEntityType);
    };

    @action
    showLocationOnTheMap = (location?: GeoPointLocation | GeoPointAddress) => {
        if (!location || !isValidLatLng(location.latitude, location.longitude)) return;
        if (this.amlCallEntity) {
            this.selectedAmlPhoneNumberId = undefined;
            this.setAmlCallEntity(undefined);
        }
        if (this.phoneCallEntity) {
            this.selectedPhoneCallId = undefined;
            this.setPhoneCallEntity(undefined);
        }
        this.refreshEntitiesAndPolylines({
            shouldRefreshLedgerEntity: true,
            shouldMoveCenter: true,
            entityType: EsriMapEntityTypeEnum.GEO_POINT_LOCATION,
            data: {
                entityData: location,
                mapCenterLongitude: location.longitude,
                mapCenterLatitude: location.latitude,
            },
        });
    };

    @action
    setShouldMoveMapCenterToLoggedInUnit = (shouldMove: boolean) => {
        this.shouldMoveMapCenterToLoggedInUnit = shouldMove;
    };

    @action
    setIsMapLoaded = (isMapLoaded: boolean) => {
        this.isMapLoaded = isMapLoaded;
    };

    @action
    addDelayedAction = (action: ViewLocationOnMap) => {
        if (this.delayedActionsQueue.length < DELAYED_ACTIONS_QUEUE_CAPACITY) {
            this.delayedActionsQueue.push(action);
        } else {
            logWarn('Delayed actions queue capacity is exceeded', {
                action,
            });
        }
    };

    @action
    resetDelayedActionQueue = () => {
        this.delayedActionsQueue = [];
    };

    @action
    getNextDelayedAction = (): ViewLocationOnMap | undefined => {
        return this.delayedActionsQueue.shift();
    };

    @action
    setAssociatedPhoneNumberId = (associatedPhoneNumberId?: number) => {
        this.associatedPhoneNumberId = associatedPhoneNumberId;
    };
}
